/**
 * Les nombres qui transforment une distance en heures, et la journée qui dit combien de camions.
 *
 * *Demande de Chris, 04/09/2026 : « intègre dans ton calcul une vitesse moyenne de 50 km/h […]
 * 80 tonnes = 17 tours de 20 minutes qui correspondent aux heures évoquées. »*
 *
 * Il parle de tours de vingt minutes là où le réglage en pose trente. **C'est exactement pour ça
 * que cet écran existe** : un contrôle qu'on ne peut pas régler est un contrôle qu'on cesse de
 * lire à la première alerte qui paraît fausse. Les trois valeurs se corrigent ici, et l'effet se
 * voit immédiatement sur toutes les lignes du tableau de bord — ce sont les mêmes trois nombres
 * pour tout le groupe.
 *
 * ## Pourquoi l'exemple chiffré est affiché sous les champs
 *
 * Un réglage de ce genre ne se juge pas dans l'abstrait : « 30 minutes » ne dit rien, «  un tour
 * de 24 min pour une carrière à 3 km » se juge en une seconde. L'écran refait donc le calcul sous
 * les yeux, avec les valeurs saisies, sur deux distances réelles du groupe — c'est le même
 * raisonnement que le « coût caché de l'arrondi » du déboursé : on montre l'effet, pas le
 * paramètre.
 */
import { useCallback, useEffect, useState } from "react";
import type { JSX } from "react";
import { formaterNombre, toursParCamion } from "@planif/moteur";
import { chargerReglagesTransport, reglerTransport } from "./api";
import type { ReglageTransport } from "./api";

/** Ce que chaque réglage est, en clair, et ce qu'il change. */
const LIBELLES: Readonly<Record<string, { titre: string; unite: string; aide: string }>> = {
  vitesse_moyenne_kmh: {
    titre: "Vitesse moyenne",
    unite: "km/h",
    aide:
      "Moyenne au sens plein : elle absorbe les traversées de village, les ronds-points et les " +
      "pistes. C'est pourquoi elle est très inférieure à une vitesse légale.",
  },
  manoeuvre_minutes: {
    titre: "Manœuvre, par tour de matière",
    unite: "min",
    aide:
      "Ce qu'un tour coûte indépendamment de sa longueur : bascule, attente à la trémie, " +
      "positionnement, benne levée. Ne se divise pas par la distance, s'ajoute.",
  },
  arrimage_minutes: {
    titre: "Chargement à la carrière, par tour",
    unite: "min",
    aide:
      "Le temps de charger la benne, à chaque tour, en plus de la manœuvre. Il s'ajoute à la " +
      "route dans le contrôle du transport ET dans le calcul qui pose les rotations : les deux " +
      "lisent le même tour. Ce réglage servait à l'arrimage d'une machine et n'était compté " +
      "nulle part ; il compte depuis le 09/09/2026.",
  },
  retour_depot_seuil_heures: {
    titre: "Retour au dépôt au-delà de",
    // « h de travail » et non « h » : le champ attend une DURÉE, et « h » tout seul se lit comme
    // une heure de la journée — Chris y a saisi 17, l'heure de sa règle, et le champ a refusé.
    unite: "h de travail sur le chantier",
    aide:
      "Ce n'est PAS une heure de la journée : 17 h ne se saisit pas ici. C'est le temps passé " +
      "par le camion sur le chantier au-delà duquel il rentre au dépôt — 7 h sur une journée de " +
      "9 h, par exemple. En dessous, il enchaîne ailleurs et son retour est imputé là-bas. " +
      "ATTENTION : ce réglage ne pilote plus aucun calcul depuis le 09/09/2026, le retour du " +
      "soir n'étant plus compté nulle part.",
  },
  journee_chauffeur_heures: {
    titre: "Journée du chauffeur",
    unite: "h",
    aide:
      "Un chauffeur fait 9 h à 9 h 30, et non les 8 h d'une équipe de chantier. Décimales " +
      "admises : 9,5 vaut neuf heures et demie. Elle décide combien de chargements complets un " +
      "camion tient dans sa journée, donc combien de camions partent et combien d'heures porte " +
      "chaque pastille — pas le total des heures, qui vient du tonnage. La demi-journée de 4 h " +
      "du planning n'est pas touchée.",
  },
};

/** Deux distances réelles du groupe, pour juger un réglage sur un cas et non dans l'abstrait. */
const EXEMPLES = [
  { km: 3, ou: "un site à 3 km" },
  { km: 47.9, ou: "Pouydesseaux → Maillas, 47,9 km" },
];

/** « 9,5 h » se dit « 9 h 30 » : c'est ainsi que Chris en parle, et 9,5 se lit mal. */
function direUneJournee(texte: string): string {
  const heures = Number(texte.replace(",", "."));
  if (!(heures > 0)) return "—";
  const minutes = Math.round(heures * 60);
  const reste = minutes % 60;
  const h = Math.floor(minutes / 60);
  return reste === 0 ? `${String(h)} h` : `${String(h)} h ${String(reste).padStart(2, "0")}`;
}

/**
 * Combien de chargements COMPLETS un camion tient dans la journée du chauffeur.
 *
 * **Le compte vient du moteur**, par la fonction même que le calcul du transport appelle : un
 * écran qui montre l'effet d'un réglage doit annoncer le nombre que le calcul posera, et le
 * refaire ici finirait un jour par en annoncer un autre.
 */
function chargementsParCamion(
  km: number,
  vitesse: number,
  minutes: number,
  journee: string,
): string {
  if (!(vitesse > 0) || !(Number(journee.replace(",", ".")) > 0)) return "—";
  const tour = (2 * km) / vitesse + minutes / 60;
  if (!(tour > 0)) return "—";
  const compte = toursParCamion(tour.toFixed(6), journee.replace(",", "."));
  return `${String(compte)} chargement${compte > 1 ? "s" : ""}`;
}

function direUnTour(km: number, vitesse: number, minutes: number): string {
  if (!(vitesse > 0)) return "—";
  const heures = (2 * km) / vitesse + minutes / 60;
  const dit = heures < 1.5 ? `${String(Math.round(heures * 60))} min` : `${formaterNombre(heures.toFixed(4), 1)} h`;
  return dit;
}

export function ReglagesTransport(): JSX.Element {
  const [reglages, setReglages] = useState<readonly ReglageTransport[] | null>(null);
  const [saisies, setSaisies] = useState<Record<string, string>>({});
  const [erreur, setErreur] = useState<string | null>(null);
  const [enAttente, setEnAttente] = useState(false);

  const charger = useCallback(async () => {
    try {
      const lu = await chargerReglagesTransport();
      setReglages(lu.reglages);
      setSaisies(Object.fromEntries(lu.reglages.map((r) => [r.cle, r.valeur ?? ""])));
      setErreur(null);
    } catch (e) {
      setErreur(e instanceof Error ? e.message : String(e));
    }
  }, []);

  useEffect(() => {
    void charger();
  }, [charger]);

  const enregistrer = async (cle: string): Promise<void> => {
    const saisie = Number((saisies[cle] ?? "").replace(",", "."));
    if (!Number.isFinite(saisie)) {
      setErreur("Cette valeur doit être un nombre.");
      return;
    }
    setEnAttente(true);
    try {
      await reglerTransport(cle, saisie);
      await charger();
      setErreur(null);
    } catch (e) {
      setErreur(e instanceof Error ? e.message : String(e));
    } finally {
      setEnAttente(false);
    }
  };

  const nombre = (cle: string): number => Number((saisies[cle] ?? "0").replace(",", "."));
  const vitesse = nombre("vitesse_moyenne_kmh");
  const manoeuvre = nombre("manoeuvre_minutes");
  const chargement = nombre("arrimage_minutes");
  // En TEXTE : « 9,5 » ne doit pas transiter par un flottant, le moteur le refuse à raison.
  const journee = saisies["journee_chauffeur_heures"] ?? "";

  return (
    <section className="reglages-transport">
      <h3>Le calcul du contrôle</h3>
      <p className="etat">
        Ces nombres transforment une distance en heures, et c'est avec eux que le tableau de bord
        juge le chiffrage de <strong>tous</strong> les chantiers. Un tour vaut{" "}
        <strong>2 × la distance ÷ la vitesse, plus la manœuvre et le chargement</strong> — aller et
        retour, parce que le camion revient à vide. La journée du chauffeur, elle, ne change aucune
        heure : elle dit combien de tours tiennent dans un jour, donc combien de camions partent.
      </p>

      {erreur !== null && <p className="alerte">{erreur}</p>}
      {reglages === null && erreur === null && <p>Lecture des réglages…</p>}

      {reglages !== null && (
        <table>
          <thead>
            <tr>
              <th>Réglage</th>
              <th className="nombre">Valeur</th>
              <th />
              <th>Ce qu'il est</th>
            </tr>
          </thead>
          <tbody>
            {reglages.map((r) => {
              const mots = LIBELLES[r.cle];
              return (
                <tr key={r.cle}>
                  <td>
                    <strong>{mots?.titre ?? r.cle}</strong>
                    {/*
                      Une valeur jamais réglée se DIT, elle ne se devine pas : le contrôle
                      retombe alors sur son défaut, et un champ vide sans explication laisserait
                      croire que le calcul ne tourne pas.
                    */}
                    {r.valeur === null && (
                      <span className="note note-ligne note--alerte">
                        jamais réglé — le contrôle utilise sa valeur de repli
                      </span>
                    )}
                  </td>
                  <td className="nombre">
                    <input
                      className="saisie"
                      value={saisies[r.cle] ?? ""}
                      inputMode="decimal"
                      size={6}
                      disabled={enAttente}
                      onChange={(e) => {
                        setSaisies((s) => ({ ...s, [r.cle]: e.target.value }));
                      }}
                    />{" "}
                    {mots?.unite ?? ""}
                  </td>
                  <td>
                    <button
                      type="button"
                      disabled={enAttente || (saisies[r.cle] ?? "") === (r.valeur ?? "")}
                      onClick={() => {
                        void enregistrer(r.cle);
                      }}
                    >
                      Enregistrer
                    </button>
                  </td>
                  <td className="note">{mots?.aide ?? ""}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      {/*
        **L'effet, sous les champs.** « 30 minutes » ne se juge pas ; « un tour de 24 min pour un
        site à 3 km » se juge en une seconde. Recalculé à la frappe, avant même d'enregistrer.
      */}
      {reglages !== null && (
        <p className="etat">
          Avec ces valeurs, un tour de matière durerait{" "}
          {EXEMPLES.map((e, n) => (
            <span key={e.km}>
              {n > 0 && " · "}
              {/*
                LE CHARGEMENT ENTRE DANS L'EXEMPLE AUSSI.

                Il entre dans le tour depuis le 09/09, et cette ligne ne l'avait pas suivi : elle
                annonçait dix minutes de moins que le calcul, sur chacun des deux exemples. Un
                écran qui montre l'effet d'un réglage doit montrer le MÊME tour que le calcul,
                sinon c'est lui qu'on croira.
              */}
              <strong>{direUnTour(e.km, vitesse, manoeuvre + chargement)}</strong> pour {e.ou}
            </span>
          ))}
          . Un chargement de 80 T avec un porteur de 17 T demande{" "}
          <strong>{String(Math.ceil(80 / 17))} tours</strong>.
        </p>
      )}

      {/*
        LA JOURNÉE DU CHAUFFEUR SE JUGE EN CHARGEMENTS, pas en heures (075, 09/09/2026).

        « 9 h » ne dit rien ; « 4 chargements par camion pour une carrière à 38,9 km » se juge en
        une seconde — et c'est le nombre qui décide combien de camions partent. Les deux distances
        des exemples ci-dessus sont reprises ici pour que le passage de 9 à 9,5 se voie tout de
        suite : sur certaines distances il ajoute un chargement, sur d'autres rien du tout.
      */}
      {reglages !== null && (
        <p className="etat">
          Un chauffeur roule <strong>{direUneJournee(journee)}</strong>, là où une équipe de
          chantier fait 8 h. Il tient donc{" "}
          {EXEMPLES.map((e, n) => (
            <span key={e.km}>
              {n > 0 && " · "}
              <strong>{chargementsParCamion(e.km, vitesse, manoeuvre + chargement, journee)}</strong>{" "}
              pour {e.ou}
            </span>
          ))}
          . C'est ce nombre qui dit combien de camions il faut, jamais le total des heures — celui-là
          vient du tonnage.
        </p>
      )}
    </section>
  );
}
